import { isDevEnv, isDownloadContextOnLlmCall } from "../../../../shared/util";
import { FhirBundleSdk, getClinicalDateRange, Resource } from "@metriport/fhir-sdk";
import { buildDayjs } from "@metriport/shared/common/date";
import _ from "lodash";
import { determineOptimalTimeframe, SlimResource } from "../clean-for-summary";
import { BaseSeries } from "../table-transforms/series-utils";

/**
 * Checks if the application is running in a local development environment
 */
function isDebugLocalEnvironment(): boolean {
  return isDevEnv() && isDownloadContextOnLlmCall();
}

/**
 * Downloads data as a JSON file in the browser
 */
function downloadAsFile(data: unknown, filename: string): void {
  try {
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const fullFilename = `${timestamp}-${filename}.json`;

    // Create blob with JSON data
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });

    // Create download URL
    const url = URL.createObjectURL(blob);

    // Create temporary anchor element and trigger download
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fullFilename;
    anchor.style.display = "none";

    // Append to body, click, and remove
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    // Clean up the URL object
    URL.revokeObjectURL(url);

    console.log(`Download initiated for: ${fullFilename}`);
  } catch (error) {
    console.error("Failed to download file:", error);
    // Fallback to console logging
    console.log("Fallback - logging to console:");
    console.log(JSON.stringify(data, null, 2));
  }
}

/**
 * Logs the IDs of all FHIR resources in a series by retrieving them from the bundle.
 * This function takes each resource from the series, gets the corresponding resource
 * from the bundle using the SDK, and logs its ID.
 *
 * @param series - The series containing resources to process
 * @param bundle - The FHIR bundle SDK instance to retrieve resources from
 */
export function buildSeriesResourceContext(
  series: BaseSeries<unknown> | null,
  bundle: FhirBundleSdk | undefined
): string {
  if (!series || !bundle) {
    console.log("Series or bundle is not available");
    return "";
  }

  const context: SlimResource[] = [];

  const firstDateRange = getClinicalDateRange(_.first(series.resources) as Resource);
  const lastDateRange = getClinicalDateRange(_.last(series.resources) as Resource);

  if (!firstDateRange || !lastDateRange) {
    throw new Error("First or last date range not found in series resources - failing");
  }

  // Check if first and last are within a year of each other
  const firstDate = buildDayjs(firstDateRange.startDate);
  const lastDate = buildDayjs(lastDateRange.startDate);
  const isWithinOneYear = Math.abs(lastDate.diff(firstDate, "year", true)) <= 1;

  if (isWithinOneYear) {
    // If within a year, just use last (it will reach backwards and catch first)
    const { finalSlimPayloadBundle: lastResourceSlimBundle } = determineOptimalTimeframe(
      bundle.toObject(),
      lastDate
    );
    context.push(...lastResourceSlimBundle);
  } else {
    // If more than a year apart, get context for both
    const { finalSlimPayloadBundle: firstResourceSlimBundle } = determineOptimalTimeframe(
      bundle.toObject(),
      firstDate
    );
    const { finalSlimPayloadBundle: lastResourceSlimBundle } = determineOptimalTimeframe(
      bundle.toObject(),
      lastDate
    );
    context.push(...firstResourceSlimBundle, ...lastResourceSlimBundle);
  }

  // Download file if in local environment, otherwise log to console
  if (isDebugLocalEnvironment()) {
    const filename = `series-resource-context-${series.code}`;
    const strippedContext = context.map(r => FhirBundleSdk.stripNonClinicalData(r as Resource));
    downloadAsFile(strippedContext, filename);
  } else {
    console.log("Context gathered without errors.");
  }

  return JSON.stringify(context);
}

/**
 * Builds context for a single FHIR resource by gathering surrounding resources
 * from the bundle based on the resource's date.
 *
 * @param resource - The single FHIR resource to build context for
 * @param bundle - The FHIR bundle SDK instance to retrieve surrounding resources from
 * @returns JSON string of surrounding context resources
 */
export function buildSingleResourceContext(
  resource: unknown | null,
  bundle: FhirBundleSdk | undefined
): string {
  if (!resource || !bundle) {
    console.log("Resource or bundle is not available");
    return "";
  }

  const resourceObj = resource as Resource;

  console.log(`Processing single resource: ${resourceObj.resourceType} (${resourceObj.id})`);

  const context: SlimResource[] = [];

  // Get the date range for this resource
  const dateRange = getClinicalDateRange(resourceObj);

  if (!dateRange) {
    console.log("Date range not found in resource - using current date");
    // Fallback to current date if resource has no date
    const { finalSlimPayloadBundle } = determineOptimalTimeframe(
      bundle.toObject(),
      buildDayjs(new Date().toISOString())
    );
    context.push(...finalSlimPayloadBundle);
  } else {
    // Use the resource's date to get surrounding context
    const resourceDate = buildDayjs(dateRange.startDate);
    const { finalSlimPayloadBundle } = determineOptimalTimeframe(bundle.toObject(), resourceDate);
    context.push(...finalSlimPayloadBundle);
  }

  // Download file if in local environment, otherwise log to console
  if (isDebugLocalEnvironment()) {
    const filename = `single-resource-context-${resourceObj.resourceType}-${resourceObj.id}`;
    const strippedContext = context.map(r => FhirBundleSdk.stripNonClinicalData(r as Resource));
    downloadAsFile(strippedContext, filename);
  } else {
    console.log("Context gathered without errors.");
  }

  return JSON.stringify(context);
}
